import {
  ai_charge,
  ai_move,
  ai_run,
  ai_stand,
  ai_walk,
  monster_think,
} from '../../ai/index.js';
import {
  DeadFlag,
  Entity,
  MonsterFrame,
  MonsterMove,
  MoveType,
  Solid,
  EntityFlags,
  PainCallback
} from '../entity.js';
import { SpawnContext, SpawnRegistry } from '../spawn.js';
import { throwGibs, GIB_METALLIC } from '../gibs.js';
import { monster_fire_rocket, monster_fire_bullet, monster_fire_blaster } from './attack.js';
import { normalizeVec3, subtractVec3, Vec3, angleVectors, scaleVec3, addVec3, ZERO_VEC3, lengthVec3 } from '@quake2ts/shared';
import { DamageMod } from '../../combat/damageMods.js';
import { rangeTo, infront, visible } from '../../ai/perception.js';
import { EntitySystem } from '../system.js';

const MONSTER_TICK = 0.1;

// Constants - Source: rerelease/m_boss2.c
const BOSS2_HEALTH = 3000;
const BOSS2_MASS = 1000;
const BOSS2_VIEWHEIGHT = 64;
const BOSS2_GIB_HEALTH = -80;
const BOSS2_MACHINEGUN_RANGE = 125;
const BOSS2_MACHINEGUN_CHANCE = 0.6;
const BOSS2_MACHINEGUN_REFIRE_CHANCE = 0.7;
const BOSS2_ROCKET_DAMAGE = 50;
const BOSS2_ROCKET_SPEED = 500;
const BOSS2_MACHINEGUN_DAMAGE = 6;
const BOSS2_MACHINEGUN_KICK = 4;
const BOSS2_MACHINEGUN_HSPREAD = 0.1;
const BOSS2_MACHINEGUN_VSPREAD = 0.05;

// Offsets
const BOSS2_ROCKET_OFFSET_1: Vec3 = { x: 0, y: 30, z: -15 };
const BOSS2_ROCKET_OFFSET_2: Vec3 = { x: 0, y: 15, z: 0 };
const BOSS2_ROCKET_OFFSET_3: Vec3 = { x: 0, y: -15, z: 0 };
const BOSS2_ROCKET_OFFSET_4: Vec3 = { x: 0, y: -30, z: -15 };

const BOSS2_MG_LEFT_OFFSET: Vec3 = { x: 30, y: 20, z: 0 };
const BOSS2_MG_RIGHT_OFFSET: Vec3 = { x: 30, y: -20, z: 0 };

// Wrappers
function monster_ai_stand(self: Entity, dist: number, context: any): void {
  ai_stand(self, dist, context);
}

function monster_ai_walk(self: Entity, dist: number, context: any): void {
  ai_walk(self, dist, context);
}

function monster_ai_run(self: Entity, dist: number, context: any): void {
  ai_run(self, dist, context);
}

function monster_ai_charge(self: Entity, dist: number, context: any): void {
  ai_charge(self, dist, context);
}

function monster_ai_move(self: Entity, dist: number, context: any): void {
  ai_move(self, dist);
}

// Forward declarations
let stand_move: MonsterMove;
let walk_move: MonsterMove;
let run_move: MonsterMove;
let attack_pre_mg_move: MonsterMove;
let attack_mg_move: MonsterMove;
let attack_post_mg_move: MonsterMove;
let attack_rocket_move: MonsterMove;
let pain_heavy_move: MonsterMove;
let pain_light_move: MonsterMove;
let death_move: MonsterMove;

function boss2_stand(self: Entity): void {
  self.monsterinfo.current_move = stand_move;
}

function boss2_walk(self: Entity): void {
  self.monsterinfo.current_move = walk_move;
}

function boss2_run(self: Entity): void {
  if (self.enemy && self.enemy.health > 0) {
    self.monsterinfo.current_move = run_move;
  } else {
    self.monsterinfo.current_move = stand_move;
  }
}

function boss2_attack(self: Entity, context: EntitySystem): void {
    if (!self.enemy) return;

    const range = rangeTo(self, self.enemy);

    if (range <= BOSS2_MACHINEGUN_RANGE || context.rng.frandom() <= BOSS2_MACHINEGUN_CHANCE) {
        self.monsterinfo.current_move = attack_pre_mg_move;
    } else {
        self.monsterinfo.current_move = attack_rocket_move;
    }
}

function boss2_attack_mg(self: Entity): void {
    self.monsterinfo.current_move = attack_mg_move;
}

function boss2_reattack_mg(self: Entity, context: EntitySystem): void {
    if (self.enemy && infront(self, self.enemy) && context.rng.frandom() <= BOSS2_MACHINEGUN_REFIRE_CHANCE) {
        boss2_attack_mg(self);
    } else {
        self.monsterinfo.current_move = attack_post_mg_move;
    }
}

function getProjectedOffset(self: Entity, offset: Vec3): Vec3 {
    const { forward, right, up } = angleVectors(self.angles);
    const start = { ...self.origin };

    const x = scaleVec3(forward, offset.x);
    const y = scaleVec3(right, offset.y);
    const z = scaleVec3(up, offset.z);

    return addVec3(addVec3(addVec3(start, x), y), z);
}

function boss2_fire_mg(self: Entity, context: any): void {
    if (!self.enemy) return;

    context.engine.sound?.(self, 1, 'boss2/machgun.wav', 1, 1, 0);

    // Fire left
    const startL = getProjectedOffset(self, BOSS2_MG_LEFT_OFFSET);
    const dirL = normalizeVec3(subtractVec3(self.enemy.origin, startL));
    monster_fire_bullet(self, startL, dirL, BOSS2_MACHINEGUN_DAMAGE, BOSS2_MACHINEGUN_KICK, BOSS2_MACHINEGUN_HSPREAD, BOSS2_MACHINEGUN_VSPREAD, 0, context, DamageMod.MACHINEGUN);

    // Fire right
    const startR = getProjectedOffset(self, BOSS2_MG_RIGHT_OFFSET);
    const dirR = normalizeVec3(subtractVec3(self.enemy.origin, startR));
    monster_fire_bullet(self, startR, dirR, BOSS2_MACHINEGUN_DAMAGE, BOSS2_MACHINEGUN_KICK, BOSS2_MACHINEGUN_HSPREAD, BOSS2_MACHINEGUN_VSPREAD, 0, context, DamageMod.MACHINEGUN);
}

function boss2_fire_rocket(self: Entity, context: any): void {
    if (!self.enemy) return;

    // C code fires different rockets based on frame or sequence.
    // We will just fire based on current frame index or passed context if possible,
    // but here we can just fire a spread or one by one.
    // The animation calls this function.
    // We'll approximate by firing from one of the pods randomly or cycling.

    const offsets = [BOSS2_ROCKET_OFFSET_1, BOSS2_ROCKET_OFFSET_2, BOSS2_ROCKET_OFFSET_3, BOSS2_ROCKET_OFFSET_4];
    const offset = offsets[Math.floor(context.rng.frandom() * offsets.length)];

    const start = getProjectedOffset(self, offset);
    const forward = normalizeVec3(subtractVec3(self.enemy.origin, start));

    monster_fire_rocket(self, start, forward, BOSS2_ROCKET_DAMAGE, BOSS2_ROCKET_SPEED, 0, context);
}

function boss2_pain(self: Entity, other: Entity | null, kick: number, damage: number, context: any): void {
    if (self.timestamp < (self.pain_finished_time || 0)) return;
    self.pain_finished_time = self.timestamp + 3.0;

    context.engine.sound?.(self, 0, 'boss2/bs2pain1.wav', 1, 1, 0);

    if (damage < 10) {
        self.monsterinfo.current_move = pain_light_move;
    } else if (damage < 30) {
        self.monsterinfo.current_move = pain_light_move;
    } else {
        self.monsterinfo.current_move = pain_heavy_move;
    }
}

function boss2_die(self: Entity, context: any): void {
  context.engine.sound?.(self, 0, 'boss2/bs2deth1.wav', 1, 1, 0);
  self.monsterinfo.current_move = death_move;
}

function boss2_dead(self: Entity): void {
  self.monsterinfo.nextframe = death_move.lastframe;
  self.nextthink = -1;
}

// Frames
// Walk/Run: 20 frames (1-20)
const walk_frames: MonsterFrame[] = Array.from({ length: 20 }, () => ({ ai: monster_ai_walk, dist: 10 }));
walk_move = { firstframe: 0, lastframe: 19, frames: walk_frames, endfunc: boss2_walk };

run_move = { firstframe: 0, lastframe: 19, frames: walk_frames, endfunc: boss2_run }; // Uses same frames

// Stand: 21 frames (30-50)
const stand_frames: MonsterFrame[] = Array.from({ length: 21 }, () => ({ ai: monster_ai_stand, dist: 0 }));
stand_move = { firstframe: 29, lastframe: 49, frames: stand_frames, endfunc: boss2_stand };

// Attack Pre MG: 9 frames (51-59)
const attack_pre_mg_frames: MonsterFrame[] = Array.from({ length: 9 }, (_, i) => ({
    ai: monster_ai_charge,
    dist: 2,
    think: (i === 8) ? boss2_attack_mg : null
}));
attack_pre_mg_move = { firstframe: 50, lastframe: 58, frames: attack_pre_mg_frames, endfunc: null };

// Attack MG: 6 frames (60-65) - Loops
const attack_mg_frames: MonsterFrame[] = Array.from({ length: 6 }, () => ({
    ai: monster_ai_charge,
    dist: 2,
    think: boss2_fire_mg
}));
attack_mg_move = { firstframe: 59, lastframe: 64, frames: attack_mg_frames, endfunc: boss2_reattack_mg };

// Attack Post MG: 4 frames (66-69)
const attack_post_mg_frames: MonsterFrame[] = Array.from({ length: 4 }, () => ({ ai: monster_ai_charge, dist: 2 }));
attack_post_mg_move = { firstframe: 65, lastframe: 68, frames: attack_post_mg_frames, endfunc: boss2_run };

// Attack Rocket: 21 frames (70-90)
const attack_rocket_frames: MonsterFrame[] = Array.from({ length: 21 }, (_, i) => ({
    ai: monster_ai_charge,
    dist: 2,
    think: (i === 12) ? boss2_fire_rocket : null // Fire around middle
}));
// To match C behavior of firing 4 rockets, we should fire multiple times
// C code: frame 12 calls Boss2Rocket which fires 4 rockets in spread.
attack_rocket_move = { firstframe: 69, lastframe: 89, frames: attack_rocket_frames, endfunc: boss2_run };


// Pain Heavy: 18 frames (91-108)
const pain_heavy_frames: MonsterFrame[] = Array.from({ length: 18 }, () => ({ ai: monster_ai_move, dist: 0 }));
pain_heavy_move = { firstframe: 90, lastframe: 107, frames: pain_heavy_frames, endfunc: boss2_run };

// Pain Light: 4 frames (109-112)
const pain_light_frames: MonsterFrame[] = Array.from({ length: 4 }, () => ({ ai: monster_ai_move, dist: 0 }));
pain_light_move = { firstframe: 108, lastframe: 111, frames: pain_light_frames, endfunc: boss2_run };

// Death: 49 frames (113-161)
const death_frames: MonsterFrame[] = Array.from({ length: 49 }, () => ({ ai: monster_ai_move, dist: 0 }));
death_move = { firstframe: 112, lastframe: 160, frames: death_frames, endfunc: boss2_dead };


export function SP_monster_boss2(self: Entity, context: SpawnContext): void {
  self.classname = 'monster_boss2';
  self.model = 'models/monsters/boss2/tris.md2';

  // Gibs
  // context.precacheModel('models/monsters/boss2/gibs/chaingun.md2');
  // ... remove precacheModel calls as they don't exist on context ...

  self.mins = { x: -56, y: -56, z: 0 };
  self.maxs = { x: 56, y: 56, z: 80 };
  self.movetype = MoveType.Step;
  self.solid = Solid.BoundingBox;
  if (context.health_multiplier) {
    self.health = BOSS2_HEALTH * context.health_multiplier;
  } else {
    self.health = BOSS2_HEALTH;
  }
  self.max_health = self.health;
  self.mass = BOSS2_MASS;
  self.takedamage = true;
  self.flags |= EntityFlags.Fly;
  self.viewheight = BOSS2_VIEWHEIGHT;

  self.pain = (self, other, kick, damage) => {
    // Skin change
    if (self.health < (self.max_health / 2)) {
      self.skin = 1;
    } else {
      self.skin = 0;
    }
    boss2_pain(self, other, kick, damage, context.entities);
  };

  self.die = (self, inflictor, attacker, damage, point) => {
    self.deadflag = DeadFlag.Dead;
    self.solid = Solid.Not;

    if (self.health < BOSS2_GIB_HEALTH) {
        context.entities.sound?.(self, 0, 'misc/udeath.wav', 1, 1, 0);
        throwGibs(context.entities, self.origin, damage, GIB_METALLIC);
        context.entities.free(self);
        return;
    }

    boss2_die(self, context.entities);
  };

  self.monsterinfo.stand = boss2_stand;
  self.monsterinfo.walk = boss2_walk;
  self.monsterinfo.run = boss2_run;
  self.monsterinfo.attack = (ent) => boss2_attack(ent, context.entities);
  self.monsterinfo.sight = (self, other) => {
      context.entities.sound?.(self, 0, 'boss2/sight.wav', 1, 1, 0);
  };

  self.think = monster_think;

  boss2_stand(self);
  self.nextthink = self.timestamp + MONSTER_TICK;
}

export function registerBoss2Spawns(registry: SpawnRegistry): void {
  registry.register('monster_boss2', SP_monster_boss2);
}
